import { Box, Button, Card, CardContent, Chip, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { DocumentData } from 'firebase/firestore';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import useLocales from 'hooks/useLocales';

// ----------------------------------------------------------------------

export const RootStyle = styled(Card)(({ theme }) => ({
    textAlign: 'center',
    overflow: 'visible',
    [theme.breakpoints.up('md')]: {
        height: '100%',
        display: 'flex',
        textAlign: 'left',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}));

export const StyledChip = styled(Chip)(({ theme }) => ({
    textAlign: 'center',
    position: 'absolute',
    top: '16px',
    right: '-15px',
    background: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    textTransform: 'uppercase',
    borderRadius: '50px',
    letterSpacing: '1px',
    paddingLeft: '15px',
    paddingRight: '15px',
}));

// ----------------------------------------------------------------------

interface ModuleCardProps {
    module: DocumentData;
}

const ModuleCard: React.FunctionComponent<ModuleCardProps> = ({ module }) => {
    const { translate, currentLang } = useLocales();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <RootStyle>
            <CardContent
                sx={{
                    p: { md: 3 },
                    pl: { md: 5 },
                    color: 'grey.800',
                }}
            >
                {isMobile && module.image && (
                    <Box
                        sx={{
                            p: { md: 3 },
                            pb: { xs: 3 },
                            margin: { xs: 'auto', md: 'inherit' },
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        {module.label && <StyledChip label={module.label[currentLang.value]} />}
                        <img src={module.image} alt={translate('module_illustration')} height={96} />
                    </Box>
                )}

                <Typography gutterBottom variant='h5'>
                    {module.title}
                </Typography>

                <Typography
                    variant='body2'
                    sx={{ pb: { xs: 3, xl: 5 }, maxWidth: 480, mx: { xs: 'auto', md: 'inherit' } }}
                >
                    {module?.description}
                </Typography>

                <Button variant='contained' to={module.id} component={RouterLink}>
                    {translate('view_module')}
                </Button>
            </CardContent>

            {!isMobile && module.image && (
                <Box
                    sx={{
                        p: { md: 3 },
                        margin: 'inherit',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {module.label && <StyledChip label={module.label[currentLang.value]} />}
                    <img src={module.image} alt={translate('module_illustration')} height={128} />
                </Box>
            )}
        </RootStyle>
    );
};

export default ModuleCard;
